import clsx from 'clsx';
import debounce from 'lodash/debounce';
import useTranslation from 'next-translate/useTranslation';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';

// Utils
import { markMentionsAsRead } from '@/lib/manage/mentions';

// Hooks
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

// Components
import LoadMoreInfinite from '@/components/base/LoadMoreInfinite/LoadMoreInfinite';
import Loader from '@/components/base/Loader';
import Mention from '@/components/base/Mention';
import { Illustration } from '@/components/composite/Illustration';

// Images
import illustrationEn from './../../../../../public/images/illustrations/illustrations-empty-en.webp';
import illustrationEs from './../../../../../public/images/illustrations/illustrations-empty-es.webp';
import illustrationFr from './../../../../../public/images/illustrations/illustrations-empty-fr.webp';

const MentionsList = ({
    isLoadingMore,
    isMoreToBeLoaded,
    loadMore,
    mentions,
    mentionType,
    mutateMentions,
    unreadCount,
    onReply
}) => {
    const { activeSocialProfile } = useActiveSocialProfile();
    const [markAsReadState, setMarkAsReadState] = useState({
        displayedMentionsIds: [],
        isSubmitting: false,
        error: null
    });
    const { t } = useTranslation('conversations');
    const { t: commonT } = useTranslation('commons');

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const markDisplayedMentionsAsRead = useCallback(
        debounce((ids) => {
            markMentionsAsRead(
                setMarkAsReadState,
                mutateMentions,
                mentionType,
                activeSocialProfile.id,
                ids
            );
            setMarkAsReadState((prevState) => ({
                ...prevState,
                displayedMentionsIds: prevState?.displayedMentionsIds?.filter(
                    (id) => !ids.includes(id)
                )
            }));
        }, 200),
        []
    );

    useEffect(() => {
        // When a mention is locally designated as read (displayedMentionsIds), update its state
        if (markAsReadState?.displayedMentionsIds?.length) {
            markDisplayedMentionsAsRead(markAsReadState?.displayedMentionsIds);
        }
    }, [markAsReadState?.displayedMentionsIds, markDisplayedMentionsAsRead]);

    if (0 === mentions?.length) {
        return (
            <div className="flex flex-1 flex-col items-center space-y-2 text-icoGray-500 dark:text-icoDarkMode-wolf">
                <Illustration
                    src={{ en: illustrationEn, fr: illustrationFr, es: illustrationEs }}
                />

                <span className="text-center">{t(`mentions_tracker.no_mentions`)}</span>
            </div>
        );
    }

    const handleMarkAllAsRead = async () => {
        markMentionsAsRead(setMarkAsReadState, mutateMentions, mentionType, activeSocialProfile.id);
    };

    const handleMarkAsDisplayed = (id) => {
        setMarkAsReadState((prevState) => ({
            ...prevState,
            displayedMentionsIds: [...prevState.displayedMentionsIds, id]
        }));
    };

    return (
        <div className="relative flex-grow" id="mentions-list-wrapper">
            {'string' === typeof unreadCount || 0 < unreadCount ? (
                <button
                    className={clsx(
                        'absolute right-0 top-[-2em] flex items-center text-xs text-icoBlue lg:right-6',
                        { 'opacity-50': markAsReadState?.isSubmitting }
                    )}
                    disabled={markAsReadState?.isSubmitting}
                    onClick={handleMarkAllAsRead}>
                    {markAsReadState?.isSubmitting && <Loader className="mr-1 h-3 w-3" />}
                    {commonT('comment.mark_all_read')}
                </button>
            ) : null}
            <div className="scrollbar flex h-full flex-col justify-end space-y-3 overflow-y-auto pb-4 sm:pb-10">
                {mentions?.map((mention) => (
                    <Mention
                        key={mention?.id}
                        className="w-full rounded-md border p-4 dark:border-icoDarkMode-beton"
                        markAsDisplayed={() => handleMarkAsDisplayed(mention?.id)}
                        mention={mention}
                        onReply={onReply}
                    />
                ))}
                {isMoreToBeLoaded && (
                    <LoadMoreInfinite isLoadingMore={isLoadingMore} loadMore={loadMore} />
                )}
            </div>
        </div>
    );
};

MentionsList.propTypes = {
    isLoadingMore: PropTypes.bool,
    isMoreToBeLoaded: PropTypes.bool,
    loadMore: PropTypes.func,
    mentions: PropTypes.arrayOf(PropTypes.object),
    mentionType: PropTypes.string,
    mutateMentions: PropTypes.func,
    unreadCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onReply: PropTypes.func
};

export default MentionsList;
